// ======================================================================
// 📁 File: src/modules/ppe/pages/material/raw-material/RawMaterialListPage.jsx
// ======================================================================

import React, { useEffect, useState, useCallback, useContext } from "react";
import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import RawMaterialKpiBar from "../../../components/material/raw-material/RawMaterialKpiBar";
import RawMaterialDrawer from "./RawMaterialDrawer";
import AttributeFilterBar from "../../../components/common/AttributeFilterBar";

export default function RawMaterialListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // ✅ Pagination state
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);

  // ✅ Attribute Filters
  const [attributeFilters, setAttributeFilters] = useState([]);
  const [attributes, setAttributes] = useState([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // Load Raw Materials
  // ------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/api/ppe/masters/raw-materials", {
        params: {
          q: search || undefined,
          is_active:
            status === "ALL"
              ? undefined
              : status === "ACTIVE"
              ? 1
              : 0,
          page,
          per_page: perPage,

          attribute_filters: attributeFilters.length
            ? JSON.stringify(attributeFilters)
            : undefined,
        },
      });

      const apiData = res.data?.data || {};
      setRows(apiData.data || []);
      setTotal(apiData.pagination?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [search, status, page, perPage, attributeFilters]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, status, attributeFilters]);

  // ------------------------------------------------------------
  // ✅ Load Attributes (REAL API)
  // ------------------------------------------------------------
  useEffect(() => {
    loadAttributes();
  }, []);

  const loadAttributes = async () => {
    try {
      const res = await axiosClient.get(
        "/api/ppe/sku-attributes/entity/RAW_MATERIAL"
      );

      setAttributes(res.data?.data || []);
    } catch (e) {
      console.error("Error loading attributes", e);
    }
  };

  // ------------------------------------------------------------
  // ✅ Fetch Attribute Values (DYNAMIC)
  // ------------------------------------------------------------
  const fetchValues = async (attributeId, setValues) => {
    try {
      const res = await axiosClient.get(
        `/api/ppe/sku-attributes/${attributeId}/values`
      );

      setValues(res.data?.data || []);
    } catch (e) {
      console.error("Error loading values", e);
    }
  };

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Pagination helpers
  // ------------------------------------------------------------
  const totalPages = Math.ceil(total / perPage);

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* Header */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #2dd4bf 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Raw Material Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Material configuration for BOQ
          </div>
        </div>

        {permissions?.RAW_MATERIAL_CREATE?.view && (
          <button className="btn btn-light" onClick={openCreateDrawer}>
            + Create Raw Material
          </button>
        )}
      </div>

      {/* KPI */}
      <RawMaterialKpiBar rows={rows} />

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search Raw Material"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {/* ✅ Dynamic Attribute Filter */}
      <AttributeFilterBar
        attributes={attributes}
        filters={attributeFilters}
        setFilters={setAttributeFilters}
        fetchValues={fetchValues}
      />

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>UOM</th>
                <th>Status</th>
                <th>Used in BOQ</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No raw materials found
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.name}</td>
                  <td>{r.uom}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active === 1 ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{r.used_in_boq ? "Yes" : "No"}</td>
                  <td>
                    {permissions?.RAW_MATERIAL_EDIT?.view && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: 12,
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 13 }}>
            Showing {(page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, total)} of {total}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === 1}
              onClick={handlePrev}
            >
              Prev
            </button>

            <span style={{ padding: "4px 8px" }}>
              Page {page} / {totalPages || 1}
            </span>

            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === totalPages || totalPages === 0}
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <RawMaterialDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={load}
      />
    </div>
  );
}